'use client'

import { HamishonLogoArtwork } from './HamishonLogoArtwork'
import { useLocale, useTranslations } from 'next-intl'
import { getDefaultDirection } from '@hamishon/i18n'

interface HamishonLogoProps {
  className?: string
  style?: React.CSSProperties
  /** Override the artwork. Omit it — the locale is the right answer. */
  lang?: 'fa' | 'en'
}

/**
 * Hamishon brand logotype — Persian (همیشان) and Latin ("Hamishon") artwork.
 *
 * Which one to draw is decided here rather than by the caller. Every call site
 * used to pass `lang={language === 'en' ? 'en' : 'fa'}` from `useI18n()`, which
 * is the *legacy glossary provider* — its `language` is local state seeded from
 * localStorage and has nothing to do with the URL. So /en served English copy
 * under the Persian logotype, on every page with a header or a footer.
 *
 * The choice keys on script direction, not on an `en` equality test: a
 * right-to-left language added from the admin panel gets the Arabic-script mark
 * and any left-to-right one gets the Latin mark, with no code change — which is
 * the whole premise of adding a language without a deploy.
 */
export function HamishonLogo({ className, style, lang }: HamishonLogoProps) {
  const t = useTranslations('common')
  const locale = useLocale()
  const resolved = lang ?? (getDefaultDirection(locale) === 'rtl' ? 'fa' : 'en')

  return <HamishonLogoArtwork className={className} style={style} lang={resolved} label={t('brand.name')} />
}
